import { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Layout from '../layout/Layout';
import MessageList from './MessageList';
import InputArea from './InputArea';
import AvatarDisplay from '../avatar/AvatarDisplay';
import VoiceCommandHelp from '../voice/VoiceCommandHelp';
import { useChatIntegration } from '../../hooks/useChatIntegration';
import { copyToClipboard } from '../../utils/formatters';
import { v4 as uuidv4 } from "uuid";
import type { AvatarState, ParsedCommand } from '../../types/voice';
import useAgentCall from '../../hooks/useAgentCall';
import { useUrlParam } from '../providers/ParamsContextProvider';


export default function Chat() {

  const avatarId= useUrlParam();
  const audioRef = useRef<HTMLAudioElement>(null)
    const [sessionId] = useState<string>(() => {
    // On first render try to read a saved id
    const stored = localStorage.getItem("aiAvatarSessionId");
    if (stored) return stored;

    // Otherwise create a brand‑new id and persist it
    const freshId = uuidv4();
    localStorage.setItem("aiAvatarSessionId", freshId);
    return freshId;
  });

  // Whenever the id changes, keep it in localStorage for reloads
  useEffect(() => {
    localStorage.setItem("aiAvatarSessionId", sessionId);
  }, [sessionId]);

  const {
    messages,
    isTyping,
    isMuted,
    phonemes,
    isSpeaking,
    addMessage,
    handleSendMessage,
    stopAudio,
    setIsMuted,
    replayLastAudio,
    retryLastFailed,
    lastFailedMessage,
    lipsync,
    speechSynthesisData
  } = useChatIntegration(audioRef,sessionId, avatarId);

  const { isCallActive } = useAgentCall();


  const [avatarState, setAvatarState] = useState<AvatarState>('idle');
  const [showHelp, setShowHelp] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);


  // Handle voice commands
  const handleCommand = useCallback((command: ParsedCommand) => {
    switch (command.action) {
      case 'sendMessage':
        // Handled by InputArea
        break;
      case 'clearInput':
        // Handled by InputArea
        break;
      case 'clearHistory':
        // Clear messages - we'll need to add this to the hook
        break;
      case 'repeatLast':
        replayLastAudio();
        break;
      case 'copyLast':
        const lastMessage = [...messages].reverse().find(m => m.type === 'avatar');
        if (lastMessage) {
          copyToClipboard(lastMessage.content);
          addMessage('Mensaje copiado al portapapeles', 'system');
        }
        break;
      case 'muteAudio':
        setIsMuted(true);
        stopAudio();
        addMessage('Audio silenciado', 'system');
        break;
      case 'unmuteAudio':
        setIsMuted(false);
        addMessage('Audio activado', 'system');
        break;
      case 'stopAudio':
        stopAudio();
        break;
      case 'showHelp':
        setShowHelp(true);
        break;
      case 'resetView':
        // Handled by AvatarDisplay
        break;
      case 'zoomIn':
        // Handled by AvatarDisplay
        break;
      case 'zoomOut':
        // Handled by AvatarDisplay
        break;
      default:
        console.log('Unknown command:', command.action);
    }
  }, [messages, addMessage, stopAudio, setIsMuted, replayLastAudio, retryLastFailed]);

  // Update avatar state when typing
  useEffect(() => {
    if (isTyping) {
      setAvatarState('thinking');
    } else if (!isTyping && !isCallActive && avatarState === 'thinking') {
      setAvatarState('idle');
    }
  }, [isTyping, avatarState, isCallActive]);

  // Update avatar state when speaking
  useEffect(() => {
    if (isSpeaking && !isCallActive) {
      setAvatarState('speaking');
    } else if (!isSpeaking && !isCallActive && avatarState === 'speaking') {
      setAvatarState('idle');
    }
  }, [isSpeaking, avatarState, isCallActive]);

  // Memoize the callback to prevent useAgentCall from re-initializing on every Chat render
  const handleCallEnd = useCallback((transcript: any[]) => {
    transcript.forEach(item => {
      console.log(item)
      addMessage(item.content[0]?.text || item.content[0]?.transcript, item.role === 'user' ? 'user' : 'avatar', true);
    });
  }, [addMessage]);

  return (
    <Layout
      avatarState={avatarState}
      isListening={avatarState === 'listening'}
      isMuted={isMuted}
      onToggleMute={() => setIsMuted(!isMuted)}
    >


      <div className="flex-1 min-h-0 flex flex-col lg:flex-row relative p-4 gap-4">
        <div>
          <audio ref={audioRef} autoPlay ></audio>
        </div>

        {/* Avatar Section */}
        <div className={`
          flex-1 min-h-0 lg:w-1/2 flex flex-col
        `}>
          <AvatarDisplay
            phonemes={phonemes}
            state={avatarState}
            onStateChange={setAvatarState}
            audioRef={audioRef}
            speechSynthesisData={speechSynthesisData}
            lipsync={lipsync}
            onCallEnd={handleCallEnd}
            sessionId={sessionId}
            avatarId={avatarId}
          />
        </div>

        {/* Chat Section */}
        <div className={`
          chat-card flex flex-col
          lg:flex-1 lg:w-1/2
          justify-end
          z-10
          ${isCollapsed
            ? 'fixed bottom-0 left-0 right-0 lg:left-auto lg:right-0 lg:bottom-4 lg:max-w-1/2 z-50 m-0 rounded-t-2xl shadow-2xl'
            : ''}
          transition-all duration-300 ease-in-out
          ${isCollapsed
            ? 'h-auto'
            : 'min-h-0 shrink-0 h-[38dvh] lg:h-auto lg:flex-1'}
        `}>
          {/* Chat Header with Collapse Button */}
          <div className="flex items-center justify-between p-3 border-b border-base-300 bg-base-100 rounded-t-2xl">
            <h2 className="text-lg font-semibold text-base-content">Chat</h2>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="btn btn-sm btn-ghost btn-circle"
              aria-label={isCollapsed ? 'Expandir chat' : 'Colapsar chat'}
            >
              {isCollapsed ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>

          {/* Message List */}

          <MessageList
            messages={messages}
            isTyping={isTyping}
            onPlayAudio={(messageId) => {
              if (messages.find(m => m.id === messageId && m.type === 'avatar')) {
                replayLastAudio();
              }
            }}
            onRetry={lastFailedMessage ? retryLastFailed : undefined}
            isCollapsed={isCollapsed}
          />

          {/* Input Area - Always visible */}
          <div className="border-t border-base-300">
            <InputArea
              onSendMessage={handleSendMessage}
              onCommand={handleCommand}
              disabled={isTyping}
              placeholder="Escribe un mensaje o usa el micrófono..."
            />

          </div>
        </div>
      </div>

      {/* Voice Command Help Modal */}
      <VoiceCommandHelp
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

    </Layout>
  );
}
